import { assertInInjectionContext, DestroyRef, inject, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { filter, Observable } from 'rxjs';

import { UnsavedDialogsService } from '@core/unsaved-dialogs.service';
import { ConfirmationDialogComponent } from './confirmation-dialog.component';

/**
 * Opens the unsaved-changes confirmation and emits `true` only when the user confirms. Pass `config`
 * to override the default copy.
 *
 * Unsubscribing before the user answers closes the confirmation, so a superseded navigation or a
 * destroyed dialog doesn't leave an orphaned prompt behind.
 */
export function confirmDiscardChanges(dialog: MatDialog, config?: MatDialogConfig): Observable<boolean> {
  return new Observable<boolean>((subscriber) => {
    const confirmationRef = config ? dialog.open(ConfirmationDialogComponent, config) : dialog.open(ConfirmationDialogComponent);
    let answered = false;
    const subscription = confirmationRef.afterClosed().subscribe((confirmed) => {
      answered = true;
      subscriber.next(!!confirmed);
      subscriber.complete();
    });

    return () => {
      subscription.unsubscribe();
      if (!answered) {
        confirmationRef.close();
      }
    };
  });
}

export interface UnsavedChangesDialogOptions {
  dialogRef: MatDialogRef<unknown>;
  /** Evaluated on every close attempt and on browser back/forward, so it must read current state. */
  hasUnsavedChanges: () => boolean;
  /** What backdrop click and Escape trigger — normally the component's own `onCancel()`. */
  onDismiss: () => void;
  /** Needed only when called outside the dialog component's injection context. */
  injector?: Injector;
}

/**
 * Sets up the standard close protection for a dialog holding a form:
 * - backdrop click and Escape call `onDismiss` instead of closing the dialog directly;
 * - the returned function closes the dialog, asking for confirmation first when there are unsaved changes;
 * - browser back/forward asks the same question (via {@link UnsavedDialogsService}).
 *
 * Must run in the dialog component's injection context (constructor or field initializer) unless
 * `injector` is given.
 *
 * @returns the dialog's close action, typically called from `onCancel()`.
 */
export function protectUnsavedChanges({ dialogRef, hasUnsavedChanges, onDismiss, injector }: UnsavedChangesDialogOptions): () => void {
  if (!injector) {
    assertInInjectionContext(protectUnsavedChanges);
  }
  const resolvedInjector = injector ?? inject(Injector);
  const dialog = resolvedInjector.get(MatDialog);
  const destroyRef = resolvedInjector.get(DestroyRef);

  let confirming = false;
  const requestClose = (): void => {
    if (!hasUnsavedChanges()) {
      dialogRef.close();
      return;
    }
    if (confirming) {
      return;
    }

    confirming = true;
    confirmDiscardChanges(dialog)
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe((confirmed) => {
        confirming = false;
        if (confirmed) {
          dialogRef.close();
        }
      });
  };

  dialogRef.disableClose = true;
  dialogRef
    .backdropClick()
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() => onDismiss());
  dialogRef
    .keydownEvents()
    .pipe(
      filter((event: KeyboardEvent) => event.key === 'Escape'),
      takeUntilDestroyed(destroyRef)
    )
    .subscribe(() => onDismiss());

  resolvedInjector.get(UnsavedDialogsService).register(dialogRef, hasUnsavedChanges, destroyRef);

  return requestClose;
}
